/**
 * #2098 — online presence in the browser: the Realtime Database half of
 * cs3-chat #23.
 *
 * ══ THE CONTRACT IS cs3-chat's, NOT THIS FILE'S ══
 *
 * Every constant and the document shape below mirror
 * `App\Domain\Chat\Presence` and are enforced by
 * `firebase/database.rules.json`. The rules refuse anything else, so a drift
 * here is not a cosmetic difference — it is a write the server rejects and a
 * dot that never lights.
 *
 *   status/{uid} = { state: 'online' | 'offline',
 *                    last_changed: ServerValue.TIMESTAMP,
 *                    cid: <client uuid> | '' }
 *
 * ⚠ THIS IS THE ONE PLACE THE BROWSER WRITES TO FIREBASE, and the exception is
 * deliberate and narrow (spec §4 is "browsers only read"). The useful part of
 * presence is the DISCONNECT — a closed tab, a shut laptop, a train tunnel —
 * and only the connection's own endpoint knows when that happened.
 * `onDisconnect()` is a server-side promise Firebase runs when the socket
 * drops; through Laravel it would be a heartbeat from every open tab and a
 * worse answer. The rules allow exactly `status/{own uid}` and nothing else in
 * the database.
 *
 * ══ ⚠ A UID WITH NO PRESENCE NODE IS A PERMISSION ERROR, NOT AN EMPTY READ ══
 *
 * This is the one thing about the deployed rules that is not obvious and that
 * would otherwise fill the console. The read rule for a client user is
 *
 *     data.child('cid').val() === auth.token.cid
 *
 * so for a person who has NEVER opened chat there is no node, `cid` reads as
 * null, the comparison fails, and Firebase answers PERMISSION_DENIED rather
 * than a null snapshot. Most people in a local have never opened chat, so that
 * is the common case for a search result — and it must read as "no dot",
 * quietly. Every listener here therefore has an error handler that records
 * "unknown" and says nothing.
 *
 * Firebase is injected rather than imported so this runs under node: the
 * mistakes available here are a tab that never goes offline, a listener per
 * search keystroke, and a stale `online` believed for ever.
 */

/** The subtree, as `Presence::ROOT`. */
export const ROOT = 'status';

export const ONLINE = 'online';

export const OFFLINE = 'offline';

/**
 * `Presence::NO_CLIENT` — CS staff have no client and write the empty string.
 *
 * ⚠ NOT null. Realtime Database cannot store null: writing it DELETES the key,
 * which would fail the rules' `hasChildren(['state','last_changed','cid'])`.
 */
export const NO_CLIENT = '';

/**
 * `Presence::STALE_AFTER` — seconds an `online` may be believed.
 *
 * The reader applies it, because nothing writes at the moment belief should
 * lapse: `onDisconnect` is reliable but not instant, and a client killed in a
 * way that lost the server's promise would otherwise read as online for ever.
 */
export const STALE_AFTER = 300;

export function statusPath(uid) {
    return ROOT + '/' + uid;
}

/**
 * The document to write, as the rules will accept it.
 *
 * @param {string} state      ONLINE or OFFLINE
 * @param {string|null} cid   this user's client, null for CS staff
 * @param {*} timestamp       the SDK's `serverTimestamp()` sentinel
 */
export function presenceDoc(state, cid, timestamp) {
    if (state !== ONLINE && state !== OFFLINE) {
        throw new Error("Presence state is 'online' or 'offline'.");
    }

    return {
        state,
        // ⚠ The SERVER's clock. The rules require `last_changed === now`, so a
        // browser-supplied number is refused — which is what stops a tab
        // backdating itself to look recently seen.
        last_changed: timestamp,
        cid: cid === null || cid === undefined ? NO_CLIENT : String(cid),
    };
}

/**
 * Is this snapshot an `online` worth believing?
 *
 * Mirrors `Presence::isOnline`. `last_changed` is Firebase's milliseconds.
 */
export function isOnline(value, now) {
    const v = value || {};
    if (v.state !== ONLINE) return false;

    const ms = Number(v.last_changed);
    if (!isFinite(ms) || ms <= 0) return false;

    const seconds = (now === undefined ? Date.now() : now) / 1000;

    return (ms / 1000) > (seconds - STALE_AFTER);
}

/**
 * Which uids to start and stop watching, given what is wanted now.
 *
 * ⚠ A DIFF, NOT A REBUILD, and that is the point of having it. The panel asks
 * for "the uids on screen" on every keystroke of the search box; tearing every
 * listener down and re-attaching it would churn a subscription per character
 * and bill a read for each. Pure, so the churn can be asserted on.
 *
 * @param {Iterable<string>} watching  uids with a live listener
 * @param {Iterable<string>} wanted    uids that should have one
 * @returns {{add: string[], remove: string[]}}
 */
export function diffWatch(watching, wanted) {
    const have = new Set(watching || []);
    const want = new Set();
    (wanted || []).forEach((uid) => { if (typeof uid === 'string' && uid !== '') want.add(uid); });

    const add = [];
    want.forEach((uid) => { if (!have.has(uid)) add.push(uid); });

    const remove = [];
    have.forEach((uid) => { if (!want.has(uid)) remove.push(uid); });

    return { add, remove };
}

/**
 * The presence client.
 *
 * Firebase's database functions are passed in (`ref`, `onValue`, `set`,
 * `onDisconnect`, `serverTimestamp`) so this file never imports the SDK and
 * runs under node against fakes. `chatService.attachPresence` does the lazy
 * import and hands them over.
 *
 * @param {object} deps
 *   db            the Database instance
 *   fns           { ref, onValue, set, onDisconnect, serverTimestamp }
 *   uid           this user's chat uid
 *   cid           their client, or null for CS staff
 *   onChange      (uid, online) => void — called when a watched dot changes
 *   onOwn         (connected) => void — optional, for a "reconnecting" hint
 */
export function createPresence(deps) {
    const d = deps || {};
    const fns = d.fns || {};
    const watchers = new Map();     // uid -> unsubscribe
    const state = new Map();        // uid -> true | false (false also = unknown)
    let ownUnsub = null;
    let stopped = false;

    /**
     * Record a dot's state, and tell the caller only when that is news.
     *
     * ⚠ "OFFLINE, AND WE KNEW NOTHING" IS NOT NEWS. The panel's default for an
     * unknown uid is already no dot, and the overwhelmingly common first answer
     * is exactly that — a person who has never opened chat has no presence node
     * and the rules answer PERMISSION_DENIED (see the file header). Notifying
     * for it would be a reactive write per search result, fifty of them for one
     * search, every one setting false over false.
     *
     * Everything that changes a dot still reports: a transition either way, and
     * any `online`.
     */
    function note(uid, online) {
        const before = state.get(uid);
        state.set(uid, online);

        if (before === online) return;
        if (before === undefined && online === false) return;
        if (typeof d.onChange === 'function') d.onChange(uid, online);
    }

    /**
     * Announce ourselves, and arrange to be marked offline when the socket goes.
     *
     * ⚠ `onDisconnect` IS REGISTERED BEFORE `online` IS WRITTEN. Firebase
     * evaluates the promise at registration and executes it on the server when
     * the connection drops — so registering it second leaves a window in which
     * a tab that died immediately stays online for ever.
     */
    function announce() {
        if (stopped) return Promise.resolve(false);

        const mine = fns.ref(d.db, statusPath(d.uid));
        const offline = presenceDoc(OFFLINE, d.cid, fns.serverTimestamp());
        const online = presenceDoc(ONLINE, d.cid, fns.serverTimestamp());

        return Promise.resolve()
            .then(() => fns.onDisconnect(mine).set(offline))
            .then(() => fns.set(mine, online))
            .then(() => true)
            .catch(() => false);   // refused or offline: no dot, no error on screen
    }

    return {
        /** Start publishing our own presence. */
        start() {
            if (stopped || ownUnsub) return;

            /*
             * ⚠ `.info/connected`, NOT a one-off write. It is Firebase's own
             * connection state and it fires again on every reconnect — a laptop
             * waking from sleep has had its `onDisconnect` already executed
             * server-side, so without re-announcing on reconnect the user is
             * offline for the rest of the session while looking at a working
             * chat.
             */
            ownUnsub = fns.onValue(
                fns.ref(d.db, '.info/connected'),
                (snap) => {
                    const connected = snap && typeof snap.val === 'function' ? snap.val() === true : false;
                    if (typeof d.onOwn === 'function') d.onOwn(connected);
                    if (connected) announce();
                },
                () => { /* cannot read our own connection state: nothing to show */ },
            );
        },

        /** Watch exactly these uids — a diff, not a rebuild. */
        watch(uids) {
            if (stopped) return;

            const { add, remove } = diffWatch(watchers.keys(), uids);

            remove.forEach((uid) => {
                const off = watchers.get(uid);
                watchers.delete(uid);
                state.delete(uid);
                try { if (typeof off === 'function') off(); } catch (e) { /* already gone */ }
            });

            add.forEach((uid) => {
                // Never our own node through this path: we know our own state,
                // and the rules let us read it, but a listener on it would
                // report our own writes back as changes.
                if (uid === d.uid) return;

                try {
                    const off = fns.onValue(
                        fns.ref(d.db, statusPath(uid)),
                        (snap) => {
                            const value = snap && typeof snap.val === 'function' ? snap.val() : null;
                            note(uid, isOnline(value, Date.now()));
                        },
                        () => {
                            /*
                             * PERMISSION_DENIED for a person who has never
                             * opened chat (see the file header) — the common
                             * case, and it means "no dot", quietly. Recorded as
                             * offline so the panel stops asking.
                             */
                            note(uid, false);
                        },
                    );
                    watchers.set(uid, off);
                } catch (e) {
                    note(uid, false);
                }
            });
        },

        /** Is this person online, as far as we have been told? */
        online(uid) { return state.get(uid) === true; },

        /** Everything we are watching, for tests and for the panel's diffing. */
        watching() { return [...watchers.keys()]; },

        /**
         * Detach, and say goodbye.
         *
         * ⚠ THE EXPLICIT `offline` MATTERS EVEN THOUGH `onDisconnect` EXISTS.
         * Navigating between CS3 pages destroys the panel without closing the
         * socket, so Firebase has no disconnect to act on and the person would
         * read as online until the tab itself went. Best effort: if the write
         * does not land, `onDisconnect` is still armed.
         */
        stop() {
            stopped = true;

            watchers.forEach((off) => { try { off(); } catch (e) { /* already gone */ } });
            watchers.clear();
            state.clear();

            if (ownUnsub) {
                try { ownUnsub(); } catch (e) { /* already gone */ }
                ownUnsub = null;
            }

            try {
                return Promise.resolve(
                    fns.set(fns.ref(d.db, statusPath(d.uid)), presenceDoc(OFFLINE, d.cid, fns.serverTimestamp())),
                ).catch(() => false);
            } catch (e) {
                return Promise.resolve(false);
            }
        },
    };
}
